import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {Contact, ContactUpdate} from 'src/types/contacts';
import type {AudienceMember} from 'src/types/audience-member';
import type {ClickedNumberMessage} from 'src/chat-extension/messages';
import type {Router} from 'src/types/router';

import keyBy from 'lodash/keyBy';
import invariant from 'invariant';

import {camel, getFullName} from 'src/utils';
import {getPrimaryPhone} from 'src/utils/contacts';
import * as reduxApi from 'src/utils/redux-api-v2';
import {normalizePhone} from 'src/utils/phone';
import logger from 'src/utils/logger';
import analytics from 'src/segment-analytics';
import {selectContactByPhone} from 'src/selectors/contacts';
import {selectCurrentPhoneCountry} from 'src/selectors/phone-number-sets';
import {setMultipleRecipients} from 'src/action-creators/draft-messages';
import {showGenericError} from 'src/action-creators/modal';
import {selectDefaultPhoneNumberSet} from 'src/selectors/chat';


export const RECEIVE_MANY_CONTACTS = 'contacts/receiveMany';
export const RECEIVE_CONTACT_UPDATES = 'contacts/receiveUpdates';
export const REMOVE_CONTACT = 'contacts/remove';

export type Action = ReceiveManyContactsAction | ReceiveContactUpdatesAction;

type ReceiveManyContactsAction = {
  type: 'contacts/receiveMany';
  payload: {
    [id: string]: Contact;
  };
};

type ReceiveContactUpdatesAction = {
  type: 'contacts/receiveUpdates';
  payload: ContactUpdate[];
};

export const receiveContactUpdates = (
  contactUpdates: ContactUpdate[],
): ReceiveContactUpdatesAction => ({
  type: RECEIVE_CONTACT_UPDATES,
  payload: contactUpdates,
});

export const receiveManyContacts = (
  contacts: Contact[],
): ReceiveManyContactsAction => ({
  type: RECEIVE_MANY_CONTACTS,
  payload: keyBy(contacts, 'id'),
});

export const routeExternalContact =
  (router: Router, message: ClickedNumberMessage): ThunkAction<void> =>
  async (dispatch: Dispatch, getState: GetState) => {
    // NOTE (kyle): <= 1.14.3 versions of the extension did not
    // pass meta info with this message.
    const {payload, meta: {append} = {}} = message;
    const currentPhoneCountry = selectCurrentPhoneCountry(getState());
    const phoneNumberSet = selectDefaultPhoneNumberSet(getState());
    const phoneNumber = normalizePhone(
      payload.phoneNumber,
      currentPhoneCountry,
    );

    if (!phoneNumber) {
      dispatch(
        showGenericError({
          title: 'Unable to use contact.',
          text: `${payload.phoneNumber} is an invalid ${currentPhoneCountry} or international phone number.`,
        }),
      );
      return;
    }

    invariant(
      phoneNumber || payload.id,
      'Cannot initiate chat with this record. No id or valid phone.',
    );

    let contact: Contact | null | undefined;

    if (!contact) {
      try {
        // TODO (kyle): get thread by phone and see if it has a contact
        // or audience member.
      } catch (error: any) {}
      contact = selectContactByPhone(getState(), phoneNumber);
    }

    const memberNamesAndNums = payload.name
      ? {}
      : (await dispatch(getNamesForPhoneNumbers([phoneNumber]))) || {};

    const atsContact = {
      ...payload,
      phoneNumber,
      name: payload.name ?? memberNamesAndNums[phoneNumber],
    } as const;

    if (append) {
      const additionalRecipient = contact
        ? {contact}
        : {
            atsContact,
          };
      dispatch(
        setMultipleRecipients([
          //@ts-expect-error
          ...getState().draftMessages.recipients,
          //@ts-expect-error
          additionalRecipient,
        ]),
      );

      if (router.location.pathname !== '/messages/new') {
        router.push({
          ...router.location,
          pathname: '/messages/new',
        });
      }
    } else {
      const contactPhone = contact && getPrimaryPhone(contact);
      if (contactPhone) {
        // NOTE (kyle): preserve location query string for chrome extension
        router.push({
          ...router.location,
          pathname: `/messages/with/${contactPhone}/from/${phoneNumberSet.phone_numbers[0]}/${phoneNumberSet['selected_channel']}`,
        });
      } else {
        dispatch(
          setMultipleRecipients([
            {
              // @ts-expect-error - TS2322 - Type '{ readonly phoneNumber: string; readonly name: string; readonly id?: string; readonly type?: string; }' is not assignable to type 'AtsContact'.
              atsContact,
            },
          ]),
        );
        router.push({
          ...router.location,
          pathname: '/messages/new',
        });
      }
    }
  };

export const getNamesForPhoneNumbers =
  (
    numbers: string[],
  ): ThunkAction<
    | {
        [phoneNumber: string]: string;
      }
    | null
    | undefined
  > =>
  // @ts-expect-error - TS7030 - Not all code paths return a value.
  async (dispatch: Dispatch) => {
    try {
      const audienceMembers: {
        [phoneNumber: string]: AudienceMember[];
      } = await dispatch(
        reduxApi.post(`messages_v2/get-entities-for-phone-numbers`, {
          phone_numbers: numbers,
        }),
      );

      const memberNamesAndNums: Record<string, any> = {};
      Object.keys(audienceMembers).forEach((memberNumber) => {
        const audienceMember = camel(audienceMembers[memberNumber][0]);
        if (audienceMember) {
          memberNamesAndNums[memberNumber] = getFullName(audienceMember);
        }
      });

      return memberNamesAndNums;
    } catch (error: any) {
      analytics.track('messaging.name.fetch.fail');
      logger.error('Failed to fetch names for numbers', error);
    }
  };
